import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { compressImages, validateImage, cleanupPreviews } from '../../utils/imageCompression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Image Uploader Component with client-side compression
 * 
 * Features:
 * - Drag & drop support
 * - Multiple file selection
 * - Client-side compression before upload
 * - Preview with compression stats
 * - Progress indication
 */
function ImageUploader({ 
  category = 'spots',
  maxImages = 10,
  onImagesUploaded,
  onImagesChange, // Alternative callback for simpler use
  existingImages = [],
  className = '',
  compact = false // Compact mode for smaller UI
}) {
  const [images, setImages] = useState([]); // Local preview images
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      cleanupPreviews(images.map(img => img.preview));
    };
  }, [images]);

  const handleFileSelect = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    
    setError(null);
    
    // Check max images limit
    const remainingSlots = maxImages - images.length - existingImages.length;
    if (remainingSlots <= 0) {
      setError(`Maximum ${maxImages} images autorisées`);
      return;
    }
    
    // Limit files to remaining slots
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    
    // Validate all files first
    for (const file of filesToProcess) {
      const validation = validateImage(file);
      if (!validation.valid) {
        setError(`${file.name}: ${validation.error}`);
        return;
      }
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: filesToProcess.length });
    
    try {
      // Compress images on client side
      const compressedImages = await compressImages(filesToProcess, (current, total) => {
        setUploadProgress({ current, total, phase: 'compression' });
      });
      
      // Upload each compressed image
      const uploadedImages = [];
      for (let i = 0; i < compressedImages.length; i++) {
        const img = compressedImages[i];
        setUploadProgress({ current: i + 1, total: compressedImages.length, phase: 'upload' });
        
        const formData = new FormData();
        formData.append('file', img.compressed, img.originalName);
        formData.append('category', category);
        
        // Use CDN upload endpoint for all uploads
        const response = await fetch(`${API_URL}/api/cdn/upload`, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Erreur lors de l\'upload');
        }
        
        const result = await response.json();
        uploadedImages.push({
          id: img.id,
          url: result.url,
          filename: result.filename,
          preview: img.preview,
          originalSize: img.originalSize,
          compressedSize: img.compressedSize,
          compressionRatio: img.compressionRatio
        });
      }
      
      // Update local state
      setImages(prev => [...prev, ...uploadedImages]);
      
      // Notify parent - support both callback styles
      const allUrls = [...existingImages, ...images.map(i => i.url), ...uploadedImages.map(i => i.url)];
      
      if (onImagesChange) {
        // Simple callback with just URLs array
        onImagesChange(allUrls);
      }
      
      if (onImagesUploaded) {
        onImagesUploaded(uploadedImages.map(i => ({ url: i.url, metadata: null })));
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Erreur lors de l\'upload des images');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  }, [images, existingImages, maxImages, category, onImagesUploaded]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleRemoveImage = useCallback((indexToRemove) => {
    setImages(prev => {
      const toRemove = prev[indexToRemove];
      if (toRemove?.preview) {
        cleanupPreviews([toRemove.preview]);
      }
      return prev.filter((_, index) => index !== indexToRemove);
    });
  }, []);

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const totalImages = images.length + existingImages.length;
  const canAddMore = totalImages < maxImages;

  return (
    <div className={`${compact ? 'space-y-2' : 'space-y-4'} ${className}`}>
      {/* Drop Zone */}
      <div
        onClick={canAddMore ? openFileDialog : undefined}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-lg text-center transition-all
          ${compact ? 'p-3' : 'p-6'}
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${canAddMore ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
          ${uploading ? 'pointer-events-none' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={!canAddMore || uploading}
        />
        
        {uploading ? (
          <div className={`flex ${compact ? 'flex-row gap-2' : 'flex-col gap-2'} items-center justify-center`}>
            <Loader2 className={`${compact ? 'h-5 w-5' : 'h-8 w-8'} animate-spin text-blue-500`} />
            <p className="text-sm text-gray-600">
              {uploadProgress.phase === 'compression' 
                ? `Compression ${uploadProgress.current}/${uploadProgress.total}...`
                : `Upload ${uploadProgress.current}/${uploadProgress.total}...`
              }
            </p>
          </div>
        ) : (
          <div className={compact ? 'flex items-center gap-3' : ''}>
            <Upload className={`${compact ? 'h-5 w-5' : 'h-8 w-8 mx-auto mb-2'} text-gray-400`} />
            <div className={compact ? 'text-left' : ''}>
              <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>
                {canAddMore 
                  ? (compact ? 'Cliquez pour ajouter des photos' : 'Glissez-déposez vos images ici ou cliquez pour sélectionner')
                  : `Maximum ${maxImages} images atteint`
                }
              </p>
              {!compact && (
                <p className="text-xs text-gray-400 mt-1">
                  JPG, PNG, WebP ou GIF • Max 20 Mo par image • Compression automatique
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className={`flex items-center gap-2 ${compact ? 'p-2' : 'p-3'} bg-red-50 text-red-700 rounded-lg text-sm`}>
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button 
            onClick={() => setError(null)} 
            className="ml-auto hover:bg-red-100 p-1 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Image Previews */}
      {(images.length > 0 || existingImages.length > 0) && (
        <div className={`grid gap-2 ${compact ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'}`}>
          {/* Existing images */}
          {existingImages.map((img, index) => (
            <div key={`existing-${index}`} className={`relative group ${compact ? 'aspect-square w-16 h-16' : 'aspect-square'}`}>
              <img
                src={typeof img === 'string' ? img : img.url}
                alt={`Image ${index + 1}`}
                className="w-full h-full object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" />
            </div>
          ))}
          
          {/* Newly uploaded images */}
          {images.map((img, index) => (
            <div key={img.id} className={`relative group ${compact ? 'aspect-square w-16 h-16' : 'aspect-square'}`}>
              <img
                src={img.preview}
                alt={`Image ${existingImages.length + index + 1}`}
                className="w-full h-full object-cover rounded-lg"
              />
              
              {/* Overlay with info and delete button */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center">
                <button
                  onClick={() => handleRemoveImage(index)}
                  className={`absolute ${compact ? 'top-1 right-1 p-0.5' : 'top-2 right-2 p-1.5'} bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors`}
                >
                  <X className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
                </button>
                
                {/* Compression stats - hide in compact mode */}
                {!compact && (
                  <div className="text-white text-xs text-center px-2">
                    <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-400" />
                    <p>-{img.compressionRatio}%</p>
                    <p className="text-white/70">
                      {(img.compressedSize / 1024).toFixed(0)} Ko
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image count */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <ImageIcon className="h-4 w-4" />
          {totalImages} / {maxImages} images
        </span>
        {images.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              cleanupPreviews(images.map(img => img.preview));
              setImages([]);
            }}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            Tout supprimer
          </Button>
        )}
      </div>
    </div>
  );
}

export default ImageUploader;
